import React, { useState } from "react";
import { useApplications } from "../../../../api/applications";
import { useCandidates } from "../../../../api/candidates";
import BasePage from "../../../BasePage";
import Container from "../../../library/Container";
import Loader from "../../../library/Loader";
import ApplicantCard from "../../../ApplicantCard";
import { useModal, usePagination } from "../../../../hooks";
import Button, { ButtonIcon } from "../../../library/Button";
import CandidateCreateModal from "../../../CandidateCreateModal";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import styled from "styled-components";
import Pagination from "../../../library/Pagination";
import { JobType } from "../../../../apiResources/Job";
import RecruiterApplicantsFilterBar from "../../../RecruiterApplicantsFilterBar";

const Applications = () => {
  return (
    <BasePage title="Your Candidates">
      <Container>
        <PageContent />
      </Container>
    </BasePage>
  );
};

const getJobIdFromUrl = () => {
  const url = window.location.pathname;
  return url.split("/")[3];
};

const getEngagementIdFromUrl = () => {
  const url = window.location.pathname;
  return url.split("/")[5];
};

export type FilterData = {
  type: JobType[];
  hideApplied: "yes"[];
};

const PageContent = () => {
  const createModal = useModal(false);
  const pagination = usePagination();
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState<FilterData>({
    type: [JobType.CONTRACT, JobType.PERMANENT],
    hideApplied: ["yes"],
  });

  const { data, isLoading, refetch } = useCandidates({
    page: pagination.page,
    per: pagination.per,
    jobType: filters.type,
    excludedEngagement: filters.hideApplied.includes("yes")
      ? getEngagementIdFromUrl()
      : undefined,
    query,
  });

  const { data: applicationData, isLoading: isLoadingApplications } =
    useApplications({ engagementId: getEngagementIdFromUrl() });

  if (isLoading || isLoadingApplications) {
    return <Loader />;
  }

  const getApplicationForCandidate = (candidateId) => {
    if (isLoadingApplications) {
      return undefined;
    }

    return applicationData.applications.find(
      (application) => String(application.candidateId) === String(candidateId)
    );
  };

  const onCreateSuccess = () => {
    refetch();
    createModal.close();
  };

  const updateQuery = (value: string) => {
    setQuery(value);
  };

  return (
    <div>
      <Header>
        <Button
          type="button"
          secondary
          onClick={createModal.open}
          id="add-candidate-button"
        >
          <ButtonIcon icon={faPlus} />
          New Candidate
        </Button>
      </Header>
      <CandidateCreateModal
        isOpen={createModal.isOpen}
        onDismiss={createModal.close}
        onSuccess={onCreateSuccess}
      />
      <RecruiterApplicantsFilterBar
        filters={filters}
        query={query}
        onQueryChange={updateQuery}
        onFiltersChange={setFilters}
      />
      {data.candidates.map((candidate) => {
        return (
          <ApplicantCard
            key={candidate.id}
            candidate={candidate}
            application={getApplicationForCandidate(candidate.id)}
            newApplicationPath={`/recruiter/jobs/${getJobIdFromUrl()}/engagements/${getEngagementIdFromUrl()}/applications/new?candidate_id=${
              candidate.id
            }`}
          />
        );
      })}
      <Pagination pagination={pagination} total={data.meta.total} />
    </div>
  );
};

const Header = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 2rem;

  @media (max-width: 550px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export default Applications;
